// Gmail-style "trimmed content": split a message body into what the sender
// actually wrote and the quoted earlier messages underneath, so the
// conversation view can hide the quote behind a "•••" pill.
//
// Identical copy in webmail/src/utils/quote.js — keep the two in sync.

// "On Sat, Sep 19, 2026 at 5:19 AM PageGro <info@pagegro.com> wrote:" and
// the Outlook / forward separators, in the languages we see in practice.
const ATTR_RE = /^(?:On .{4,400}?wrote:?|-{2,}\s*Original Message\s*-{2,}|Le .{4,400}? a écrit\s*:|Am .{4,400}? schrieb.{0,120}:|El .{4,400}? escribió:|Il .{4,400}? ha scritto:)\s*$/i;

// A forward IS the message — Gmail shows forwarded content in full and only
// trims reply quotes, so a quote container that opens with the forward
// separator is left alone.
const FORWARD_RE = /^-{2,}\s*Forwarded message\s*-{2,}/i;

// Quote containers the major clients emit.
const QUOTE_SELECTOR = [
  '.gmail_quote', 'blockquote[type="cite"]', '.moz-cite-prefix', '#divRplyFwdMsg', '#appendonsend',
  '.yahoo_quoted', '#isForwardContent', 'div[data-marker="__QUOTED_TEXT__"]', '.protonmail_quote',
].join(', ');

const BLOCK_RE = /^(P|DIV|BLOCKQUOTE|TD|LI|PRE|H[1-6])$/i;
const squash = (s) => String(s || '').replace(/\s+/g, ' ').trim();

/**
 * @param html  already-sanitised HTML
 * @returns { main, quoted }  quoted is null when nothing recognisable was found
 *          or the message is nothing but a quote (then it is shown whole).
 */
export function splitQuotedHtml(html) {
  if (!html || typeof DOMParser === 'undefined') return { main: html || '', quoted: null };
  const doc  = new DOMParser().parseFromString(`<div id="__root">${html}</div>`, 'text/html');
  const root = doc.getElementById('__root');
  if (!root) return { main: html, quoted: null };

  let start = root.querySelector(QUOTE_SELECTOR);

  // An attribution line marks the start of a quote even without a container.
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    const t = squash(node.textContent);
    if (!t) continue;
    if (!ATTR_RE.test(t)) continue;
    let cand = node.parentElement;
    while (cand && cand !== root && !BLOCK_RE.test(cand.tagName) && cand.parentElement !== root) cand = cand.parentElement;
    if (!cand || cand === root) cand = node;
    // "On … wrote:" often sits inside <div><span>…</span></div>; take the
    // outermost element whose whole text is just that line.
    while (cand.parentElement && cand.parentElement !== root && squash(cand.parentElement.textContent) === t) cand = cand.parentElement;
    // Earliest of (container match, attribution match) wins.
    if (!start || (cand.compareDocumentPosition(start) & Node.DOCUMENT_POSITION_FOLLOWING)) start = cand;
    break;
  }
  if (!start) return { main: html, quoted: null };

  // Gmail nests the attribution and the blockquote in an outer .gmail_quote
  // div — climb to the outermost wrapper so they travel together.
  while (start.parentElement && start.parentElement !== root && start.parentElement.matches(QUOTE_SELECTOR)) start = start.parentElement;
  if (FORWARD_RE.test(squash(start.textContent))) return { main: html, quoted: null };

  const range = doc.createRange();
  range.setStartBefore(start);
  range.setEndAfter(root.lastChild);
  const holder = doc.createElement('div');
  holder.appendChild(range.extractContents());

  if (!squash(root.textContent) && !root.querySelector('img')) return { main: html, quoted: null };
  return { main: root.innerHTML, quoted: holder.innerHTML };
}

/** Plain-text bodies: the quote starts at the attribution line or the first ">" line. */
export function splitQuotedText(text) {
  if (!text) return { main: '', quoted: null };
  const lines = String(text).split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const twoLines = squash(`${line} ${(lines[i + 1] || '').trim()}`);
    const isAttr = ATTR_RE.test(line) || (/^On .+/.test(line) && ATTR_RE.test(twoLines));
    if (isAttr || /^>/.test(line)) {
      const main = lines.slice(0, i).join('\n').replace(/\s+$/, '');
      if (!main.trim()) return { main: text, quoted: null };
      return { main, quoted: lines.slice(i).join('\n') };
    }
  }
  return { main: text, quoted: null };
}
